import { ArrowDownLeft, ArrowUpRight, FileX2, KeyRound, Receipt } from 'lucide-react';
import { type ReactNode } from 'react';
import { Link } from 'react-router';

import { formatarData, formatarDataHora, formatarMoeda } from '@/lib/formato';
import { Dinheiro } from '@/ui/components/dinheiro';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { Kpi } from '@/ui/components/kpi';
import { Skeleton } from '@/ui/primitives/skeleton';
import { type ResumoDocumentos } from '@fiscoai/contracts';

import { useChavesRecentes, useUltimosLotes } from './api';

function Painel({
  titulo,
  link,
  children,
}: {
  titulo: string;
  link: { para: string; rotulo: string };
  children: ReactNode;
}) {
  return (
    <section className="grid content-start gap-3 rounded-painel border border-borda bg-superficie p-5">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{titulo}</h2>
        <Link to={link.para} className="text-sm text-marca hover:underline">
          {link.rotulo}
        </Link>
      </header>
      {children}
    </section>
  );
}

export function IndicadoresDoMes({
  resumo,
  erro = false,
}: {
  resumo: ResumoDocumentos | undefined;
  erro?: boolean;
}) {
  if (erro) {
    return (
      <EstadoVazio
        className="col-span-full py-6"
        tom="perigo"
        icone={<FileX2 aria-hidden />}
        titulo="Não foi possível carregar os números do mês"
        texto="Tente recarregar a página. Se continuar, avise o administrador."
      />
    );
  }
  if (!resumo) {
    return (
      <>
        <Kpi rotulo="Notas no mês" valor={null} destaque />
        <Kpi rotulo="Entradas" valor={null} />
        <Kpi rotulo="Saídas" valor={null} />
        <Kpi rotulo="Aguardando XML" valor={null} />
      </>
    );
  }
  return (
    <>
      <Kpi
        rotulo="Notas no mês"
        valor={resumo.quantidade}
        icone={<Receipt aria-hidden />}
        destaque
        detalhe={formatarMoeda(resumo.valorTotal)}
      />
      <Kpi
        rotulo="Entradas"
        valor={resumo.porDirecao.ENTRADA}
        icone={<ArrowDownLeft aria-hidden />}
      />
      <Kpi rotulo="Saídas" valor={resumo.porDirecao.SAIDA} icone={<ArrowUpRight aria-hidden />} />
      <Kpi
        rotulo="Aguardando XML"
        valor={resumo.chavesPendentes}
        icone={<KeyRound aria-hidden />}
        detalhe={`${String(resumo.porSituacao.CANCELADA)} canceladas no mês`}
      />
    </>
  );
}

export function PainelChaves({ empresaIds }: { empresaIds: readonly string[] }) {
  const chaves = useChavesRecentes(empresaIds);
  const itens = chaves.data?.items ?? [];
  return (
    <Painel titulo="Notas que faltam o XML" link={{ para: '/chaves', rotulo: 'Ver todas' }}>
      {chaves.isPending && <Skeleton className="h-40" />}
      {chaves.isSuccess && itens.length === 0 && (
        <EstadoVazio
          icone={<KeyRound />}
          titulo="Nada pendente"
          texto="Toda nota conhecida pela SEFAZ já tem o XML importado."
          className="py-6"
        />
      )}
      <ul className="divide-y divide-borda">
        {itens.map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-3 py-2.5">
            <span className="grid min-w-0 leading-tight">
              <span className="truncate font-medium">{c.emitenteNome ?? c.emitenteDocumento}</span>
              <span className="text-xs text-texto-fraco">
                Nota {Number(c.numero)}, {formatarData(c.emitidaEm)}
              </span>
            </span>
            <Dinheiro valor={c.valorTotal} />
          </li>
        ))}
      </ul>
    </Painel>
  );
}

export function PainelLotes() {
  const lotes = useUltimosLotes();
  const itens = lotes.data?.items ?? [];
  return (
    <Painel titulo="Últimos envios" link={{ para: '/importacao', rotulo: 'Importar' }}>
      {lotes.isPending && <Skeleton className="h-40" />}
      {lotes.isSuccess && itens.length === 0 && (
        <EstadoVazio
          icone={<FileX2 />}
          titulo="Nenhum envio ainda"
          texto="Comece importando os XMLs ou o relatório da SEFAZ."
          className="py-6"
        />
      )}
      <ul className="divide-y divide-borda">
        {itens.map((l) => (
          <li key={l.id}>
            <Link
              to={`/importacao/${l.id}`}
              className="flex items-center justify-between gap-3 py-2.5 hover:text-marca"
            >
              <span>
                {l.totalItens} item(ns) por {l.criadoPor.nome}
              </span>
              <span className="text-xs text-texto-fraco">{formatarDataHora(l.criadoEm)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Painel>
  );
}
